import type { Engine } from '@/components/Video/EnginePicker';

export type SttProvider = 'sarvam' | 'ai4bharat';
export type MtProvider = 'sarvam' | 'google' | 'deepl';
export type TtsProvider = 'elevenlabs' | 'sarvam' | 'ai4bharat';

export interface TranslationConfig {
  target: string;
  stt: SttProvider;
  mt: MtProvider;
  tts: TtsProvider;
  voice: string;
  voiceName?: string;
  cloneVoice: boolean;
  lipSync: boolean;
  videoMode: 'dubbed' | 'avatar' | 'lipsynced';
}

export interface TranslationTemplate {
  id: string;
  name: string;
  description: string;
  builtIn?: boolean;
  config: TranslationConfig;
}

export const sttEngines: Engine[] = [
  {
    id: 'sarvam',
    name: 'Sarvam AI',
    vendor: 'Sarvam',
    description: 'Cloud API tuned for Indian languages and English. Fast and handles noisy audio well.',
    tags: ['10+ Indian languages', 'Auto-detect', 'Fast'],
    latency: 'Fast',
    quality: 4,
    recommended: true,
  },
  {
    id: 'ai4bharat',
    name: 'AI4Bharat IndicConformer',
    vendor: 'AI4Bharat',
    description: 'Self-hosted GPU engine with strong accuracy for Indic languages and code-mixed speech.',
    tags: ['Indic focus', 'GPU required', 'Code-mixed'],
    latency: 'Slow',
    quality: 5,
  },
];

export const mtEngines: Engine[] = [
  {
    id: 'sarvam',
    name: 'Sarvam Translate',
    vendor: 'Sarvam',
    description: 'Best for Indian language pairs and colloquial / code-mixed translations.',
    tags: ['Indian languages', 'Colloquial', 'Code-mixed'],
    latency: 'Fast',
    quality: 5,
    recommended: true,
  },
  {
    id: 'google',
    name: 'Google Translate',
    vendor: 'Google',
    description: 'Broad language coverage and predictable pricing.',
    tags: ['100+ languages', 'General purpose', 'Requires API key'],
    latency: 'Fast',
    quality: 3,
  },
  {
    id: 'deepl',
    name: 'DeepL Pro',
    vendor: 'DeepL',
    description: 'Very natural phrasing for European and major global language pairs.',
    tags: ['High quality', 'Glossary', 'Requires API key'],
    latency: 'Fast',
    quality: 5,
  },
];

export const ttsEngines: Engine[] = [
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    vendor: 'ElevenLabs',
    description: 'Most expressive multilingual voices with cloning support.',
    tags: ['Voice cloning', 'Expressive', '29+ languages'],
    latency: 'Balanced',
    quality: 5,
    recommended: true,
  },
  {
    id: 'sarvam',
    name: 'Sarvam TTS',
    vendor: 'Sarvam',
    description: 'Indian-language first TTS with speed adjustment.',
    tags: ['Indian languages', 'Speed control', 'Cloud'],
    latency: 'Fast',
    quality: 4,
  },
  {
    id: 'ai4bharat',
    name: 'AI4Bharat IndicF5',
    vendor: 'AI4Bharat',
    description: 'Voice-cloned Indic TTS. Requires reference audio.',
    tags: ['Indic voices', 'Voice cloning', 'GPU required'],
    latency: 'Slow',
    quality: 4,
  },
];

export const targetLanguages = [
  { code: 'hi', name: 'Hindi' },
  { code: 'bn', name: 'Bengali' },
  { code: 'te', name: 'Telugu' },
  { code: 'mr', name: 'Marathi' },
  { code: 'ta', name: 'Tamil' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'ur', name: 'Urdu' },
  { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'or', name: 'Odia' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' },
  { code: 'en', name: 'English' },
];

export const builtInTemplates: TranslationTemplate[] = [
  {
    id: 'builtin-indic-fast',
    name: 'Fast Indic dub',
    description: 'Quick cloud-only pipeline for Hindi and other Indian languages.',
    builtIn: true,
    config: {
      target: 'hi',
      stt: 'sarvam',
      mt: 'sarvam',
      tts: 'sarvam',
      voice: '',
      cloneVoice: false,
      lipSync: false,
      videoMode: 'dubbed',
    },
  },
  {
    id: 'builtin-premium-elevenlabs',
    name: 'Premium brand dub',
    description: 'Highest quality with ElevenLabs voices and optional lip-sync.',
    builtIn: true,
    config: {
      target: 'es',
      stt: 'sarvam',
      mt: 'deepl',
      tts: 'elevenlabs',
      voice: '',
      cloneVoice: true,
      lipSync: true,
      videoMode: 'lipsynced',
    },
  },
  {
    id: 'builtin-gpu-indic',
    name: 'GPU Indic studio',
    description: 'AI4Bharat transcription and cloned Indic voice for maximum authenticity.',
    builtIn: true,
    config: {
      target: 'hi',
      stt: 'ai4bharat',
      mt: 'sarvam',
      tts: 'ai4bharat',
      voice: '',
      cloneVoice: true,
      lipSync: false,
      videoMode: 'dubbed',
    },
  },
];

export function engineName(id: string, type: 'stt' | 'mt' | 'tts'): string {
  const list = type === 'stt' ? sttEngines : type === 'mt' ? mtEngines : ttsEngines;
  return list.find((e) => e.id === id)?.name ?? id;
}

export function getLanguageName(code: string): string {
  return targetLanguages.find((l) => l.code === code)?.name ?? code;
}

const TEMPLATES_KEY = 'primeai.videoTranslation.templates.v1';

export function loadSavedTemplates(): TranslationTemplate[] {
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    if (raw) return JSON.parse(raw) as TranslationTemplate[];
  } catch {
    /* ignore corrupt storage */
  }
  return [];
}

export function persistSavedTemplates(templates: TranslationTemplate[]): void {
  try {
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
  } catch {
    /* ignore storage errors */
  }
}

export function diffConfig(a: TranslationConfig, b: TranslationConfig): string[] {
  const keys = Object.keys(a) as (keyof TranslationConfig)[];
  return keys.filter((k) => a[k] !== b[k]);
}
